<template>
    <div>
        <div class="row gutter-xs">
            <div class="panel panel-default">
                <div class="panel-body">
                    <div class="md:flex-row flex flex-col items-center justify-between">
                        <div class="md:mb-0 md:w-1/3 w-full mb-6">
                            <div class="btn-group btn-group-sm" role="group">
                                <button :disabled="true" @click="filtersPanelIsOpened = !filtersPanelIsOpened" class="btn" :class="filtersPanelIsOpened ? 'btn-primary' : 'btn-outline-primary'" type="button">
                                    <span class="icon icon-filter"></span> {{ $t('globals.filters') }}
                                    <span v-if="filtersCount > 0" class="badge">{{ filtersCount }}</span>
                                </button>
                            </div>
                        </div>
                        <div class="lg:justify-center md:justify-end md:w-1/3 flex justify-center w-full">
                            <div class="form-group form-group-sm">
                                <div class="input-with-icon w-300px mx-auto">
                                    <input class="form-control input-sm" type="search" v-model="searchQuery" :placeholder="$t('organizations.kiosk_search_placeholder')">
                                    <span class="icon icon-search input-icon"></span>
                                </div>
                            </div>
                        </div>
                        <div class="lg:block lg:w-1/3 hidden"></div>
                    </div>
                    <div v-if="filtersPanelIsOpened" class="panel mt-5px bg-gray-100">
                        <div class="panel-body text-center">
                            <form class="form form-horizontal">
                                <div class="row">
                                    <div class="form-group mb-0 space-x-2">
                                        <button @click="onFiltersSubmit" class="btn btn-outline-primary btn-sm" type="button">
                                            {{ $t('tables.apply_filters') }}
                                        </button>
                                        <button @click="onFiltersReset" type="button" class="btn btn-default btn-sm hover:border-gray-300 hover:bg-gray-300 text-gray-800 bg-gray-100 border-gray-100">
                                            {{ $t('tables.reset_filters') }}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <vue-good-table
                        mode="remote"
                        @on-page-change="onPageChange"
                        @on-sort-change="onSortChange"
                        @on-per-page-change="onPerPageChange"
                        @on-search="onSearchChange"
                        :search-options="{ enabled: true, externalQuery: serverParams.search }"
                        :sort-options="{
                            enabled: true,
                            initialSortBy: { field: 'created_at', type: 'desc' },
                        }"
                        :pagination-options="{
                            enabled: true,
                            perPage: 20,
                            setCurrentPage: table.currentPage,
                            nextLabel: $t('tables.next'),
                            prevLabel: $t('tables.previous'),
                            rowsPerPageLabel: $t('tables.rows_per_page'),
                            ofLabel: $t('tables.of'),
                            pageLabel: $t('tables.page'),
                            allLabel: $t('tables.all'),
                        }"
                        :columns="table.columns"
                        :rows="table.rows"
                        :total-rows="table.totalRecords"
                        :is-loading="table.isLoading"
                        style-class="vgt-table striped table">
                        <div slot="emptystate">
                            <div class="row p-y-md text-center">
                                <div class="col-xs-8 col-xs-offset-2">
                                    <p>{{ $t('tables.no_records_found') }}</p>
                                </div>
                            </div>
                        </div>
                        <template slot="table-column" slot-scope="props">
                            <span v-if="props.column.field == 'last_active_at'" class="inline-flex items-center">
                                {{ props.column.label }}
                                <span class="icon icon-question-circle icon-fw ml-1"
                                    v-tooltip="{
                                        content: $t('organizations.last_active_at_tip', {
                                            mailIcon: '<span class=&quot;icon icon-send&quot;></span>',
                                            lineBreak: '<br/>',
                                            notificationIcon: '<span class=&quot;icon icon-bell&quot;></span>'
                                        }),
                                        placement: 'top'
                                    }">
                                </span>
                            </span>
                            <span v-else v-html="props.column.label"></span>
                        </template>
                        <template slot="table-row" slot-scope="props">
                            <template v-if="props.column.field === 'name'">
                                <div class="flex items-center">
                                    <div class="mr-3">
                                        <organization-logo :organization="props.row" :size="'32px'"></organization-logo>
                                    </div>
                                    <div class="flex flex-col">
                                        <div class="font-bold" v-html="props.row.name"></div>
                                        <div class="text-gray-table-light text-xs font-medium">
                                            ({{ $tc('organizations.users_count', props.row.users_count, { value: props.row.users_count }) }})
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'owner'">
                                <div class="flex items-center">
                                    <div class="mr-3">
                                        <avatar :user="props.row.owner" :size="'32px'"></avatar>
                                    </div>
                                    <div class="flex flex-col">
                                        <div class="font-bold">
                                            {{ props.row.owner.full_name }}
                                        </div>
                                        <div>
                                            {{ props.row.owner.email }}
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'applications'">
                                <div class="space-x-5px flex items-center justify-start">
                                    <template v-if="props.row.applications.length > 0">
                                        <template v-for="application in props.row.applications">
                                            <div v-if="application.slug === 'crm'"
                                                class="flex items-center justify-center w-6 h-6 rounded-full"
                                                style="padding: 0.35rem;"
                                                :class="[ application.pivot.status === 'active' && application.pivot.suspension_reason === 'not_applicable' ? 'bg-purple-500' : 'bg-gray-600' ]"
                                                v-tooltip="{ content: getApplicationTooltipContent(application) }">
                                                <svg class="block w-6 h-6 text-white fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                    <path d="M8 24l3-9h-9l14-15-3 9h9l-14 15z"></path>
                                                </svg>
                                            </div>
                                            <div v-else-if="application.slug === 'tasks'"
                                                class="flex items-center justify-center w-6 h-6 rounded-full"
                                                style="padding: 0.35rem;"
                                                :class="[ application.pivot.status === 'active' && application.pivot.suspension_reason === 'not_applicable' ? 'bg-red-500' : 'bg-gray-600' ]"
                                                v-tooltip="{ content: getApplicationTooltipContent(application) }">
                                                <svg class="block w-6 h-6 text-white fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                    <path d="M4.967 15c-2.743 0-4.967-2.238-4.967-5 0-2.761 2.224-5 4.967-5h5.033v10h-5.033zm7.04 7.475c-.52-.424-.902-.994-1.095-1.637l-1.151-3.827h-5.125l1.905 5.883c.214.659.828 1.106 1.521 1.106h3.439c.358 0 .677-.225.797-.562.12-.337.015-.713-.263-.939l-.028-.024zm-.007-17.475v10c3.164.509 7.161 1.992 12 5v-20c-4.964 3.085-8.876 4.502-12 5z"></path>
                                                </svg>
                                            </div>
                                            <div v-else-if="application.slug === 'budgets'"
                                                class="flex items-center justify-center w-6 h-6 rounded-full"
                                                style="padding: 0.35rem;"
                                                :class="[ application.pivot.status === 'active' && application.pivot.suspension_reason === 'not_applicable' ? 'bg-blue-500' : 'bg-gray-600' ]"
                                                v-tooltip="{ content: getApplicationTooltipContent(application) }">
                                                <svg class="block w-6 h-6 text-white fill-current" style="height: 10px; width: 10px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                    <path d="M5 19h-4v-4h4v4zm6 0h-4v-8h4v8zm6 0h-4v-13h4v13zm6 0h-4v-19h4v19zm1 2h-24v2h24v-2z"></path>
                                                </svg>
                                            </div>
                                        </template>
                                    </template>
                                    <template v-else>
                                        {{ $t('globals.n_a') }}
                                    </template>
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'created_at'">
                                <div class="flex flex-col">
                                    <div>
                                        {{ formatDate(props.row.created_at) }}
                                    </div>
                                    <div class="text-2xs text-gray-table-light font-medium">
                                        ({{ props.row.readable_created_at }})
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field === 'last_active_at'">
                                <div class="flex flex-col">
                                    <div>
                                        {{ formatDate(props.row.last_active_at) }}
                                    </div>
                                    <div class="text-2xs text-gray-table-light font-medium">
                                        ({{ props.row.readable_last_active_at }})
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field == 'actions'">
                                <template v-if="userIsDeveloper">
                                    <div class="basic-btn-toolbar btn-toolbar flex justify-center" role="toolbar">
                                        <div class="btn-group btn-group-xs" role="group">
                                            <button @click="deleteOrganization(props.row)" class="btn btn-outline-danger" v-tooltip="{ content: $t('organizations.delete'), placement: 'top' }">
                                                <span class="icon icon-trash"></span>
                                            </button>
                                        </div>
                                    </div>
                                </template>
                            </template>
                            <span v-else v-html="props.formattedRow[props.column.field]"></span>
                        </template>
                    </vue-good-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { differenceInCalendarDays, lightFormat, isAfter, parseISO } from 'date-fns';
import Avatar from '../../user/partials/avatar.vue';
import OrganizationLogo from '../../user/partials/organization-logo.vue';

export default {
    components: {
        'avatar': Avatar,
        'organization-logo': OrganizationLogo,
    },

    props: {
        initialFilters: {
            type: Object,
            default: function() {
                return {
                    ownersOnly: false,
                };
            },
        },
        initialSort: {
            type: Array,
            default: function() {
                return [
                    { column: 'created_at', dir: 'desc' },
                ];
            },
        },
    },

    data() {
        return {
            userIsDeveloper: false,
            filtersPanelIsOpened: false,
            filters: Vue.util.extend({}, this.initialFilters),
            table: {
                columns: [
                    {
                        label: this.$t('globals.name'),
                        field: 'name',
                        html: true,
                    },
                    {
                        label: this.$t('globals.owner'),
                        field: 'owner',
                        html: true,
                    },
                    {
                        label: this.$t('globals.applications'),
                        field: 'applications',
                        sortable: false,
                        html: true,
                        tdClass: 'min-w-100px',
                    },
                    {
                        label: this.$t('organizations.created_at'),
                        field: 'created_at',
                        html: true,
                    },
                    {
                        label: this.$t('organizations.last_active'),
                        field: 'last_active_at',
                        html: true,
                    },
                    {
                        label: this.$t('globals.actions'),
                        field: 'actions',
                        html: true,
                        sortable: false,
                        hidden: !window.Globals.isDeveloper,
                        thClass: 'text-center',
                    },
                ],
                rows: [],
                totalRecords: 0,
                currentPage: 1,
                isLoading: false,
            },
            serverParams: {
                order: [],
                start: 0,
                length: 20,
                search: '',
            },
            searchQuery: '',
        };
    },

    created() {
        this.userIsDeveloper = window.Globals.isDeveloper;
    },

    computed: {
        filtersCount() {
            let count = 0;

            return count;
        },
    },

    watch: {
        searchQuery: Globals.debounce(function (value) {
            this.serverParams.search = value;
        }, 750)
    },

    methods: {
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },

        onPageChange(params) {
            this.table.currentPage = params.currentPage;
            let start = 0;
            if (params.currentPage > 1) {
                start = (params.currentPage - 1) * params.currentPerPage;
            }
            this.updateParams({ start: start });
            this.loadItems();
        },

        onPerPageChange(params) {
            this.updateParams({ length: params.currentPerPage });
            this.loadItems();
        },

        onSortChange(params) {
            const newOrder = [];
            newOrder.push({ 'column': _.head(params)['field'], 'dir': _.head(params)['type'] });
            this.updateParams({ order: newOrder });
            this.loadItems();
        },

        onSearchChange(params) {
            this.updateParams({ search: params.searchTerm });
            this.loadItems();
        },

        goToFirstPage() {
            this.table.currentPage = 1;
            this.updateParams({ start: 0 });
            this.loadItems();
        },

        loadItems() {
            this.table.isLoading = true;

            const filters = {};
            const queryParams = Object.assign({}, this.serverParams, filters);

            axios.get('/data/kiosk/organizations', { params: queryParams })
                .then(response => {
                    this.table.totalRecords = response.data.recordsFiltered;
                    this.table.rows = response.data.data;
                    this.table.isLoading = false;
                });
        },

        onFiltersSubmit() {
            this.table.currentPage = 1;
            this.updateParams({ start: 0, order: this.initialSort });
            this.loadItems();
        },

        onFiltersReset() {
            this.filters = Vue.util.extend({}, this.initialFilters);
            this.onFiltersSubmit();
            this.filtersPanelIsOpened = false;
        },

        getApplicationTooltipContent(application) {
            let tooltipContent = '';

            tooltipContent += `<div class="font-semibold text-left">${application.name}</div>`;
            const statuses = {
                'installing.not_applicable': '<span class="icon icon-spinner spin"></span> ' + this.$t('dashboard.installing').toLowerCase(),
                'installation_failed.not_applicable': '<span class="icon icon-times text-danger-500"></span> ' + this.$t('dashboard.installation_failed').toLowerCase(),
                'active.not_applicable': '<span class="icon icon-check text-success-500"></span> ' + this.$t('organization_applications.active').toLowerCase(),
                'active.unpaid_invoices': '<span class="icon icon-times text-danger-500"></span> ' + this.$t('organization_applications.suspended').toLowerCase(),
                'active.no_subscription_plan': '<span class="icon icon-times text-danger-500"></span> ' + this.$t('organization_applications.suspended').toLowerCase(),
            };
            let statusKey = application.pivot.status + '.';
            statusKey += application.pivot.status === 'active' ? application.pivot.suspension_reason : 'not_applicable';
            tooltipContent += `<table class="bg-transparent">
                <tr>
                    <td class="text-left">` + this.$t('globals.status') + ': ' + `</td>
                    <td class="text-left">&nbsp;` + statuses[statusKey] + `</td>
                </tr>`;
            if (application.pivot.status === 'active' && application.pivot.suspension_reason !== 'not_applicable') {
                const reasons = {
                    'unpaid_invoices': this.$t('organization_applications.suspended_no_subscription_plan'),
                    'no_subscription_plan': this.$t('organization_applications.suspended_no_subscription_plan'),
                };
                tooltipContent += `<tr>
                    <td></td>
                    <td class="text-left">&nbsp;` + '(' + reasons[application.pivot.suspension_reason] + ')' + `</td>
                </tr>`;
            }
            tooltipContent += '</table>';
            if (this.dateIsInFuture(application.pivot.trial_ends_at)) {
                tooltipContent += `<table class="bg-transparent">
                    <tr>
                        <td class="text-left">` + this.$t('globals.subscription_plan') + ': ' + `</td>
                        <td class="text-left">&nbsp;` + this.$t('organization_applications.trial_days_left', { days: this.getDaysLeft(application.pivot.trial_ends_at) }) + `</td>
                    </tr>
                    <tr>
                        <td></td>
                        <td class="text-left">&nbsp;` + '(' + this.$t('globals.until_date', { date: this.formatDate(application.pivot.trial_ends_at, 'dd-MM-y') }) + ')' + `</td>
                    </tr>
                </table>`;
            } else {
                if (application.pivot.subscription_plan) {
                    tooltipContent += '<div class="text-left">' + this.$t('globals.subscription_plan') + ': ' + `${application.pivot.subscription_plan.name} (€${application.pivot.subscription_plan.price})` + '</div>';
                } else {
                    tooltipContent += '<div class="text-left">' + this.$t('globals.subscription_plan') + ': ' + this.$t('globals.n_a') + '</div>';
                }
            }

            return tooltipContent;
        },

        deleteOrganization(organization) {
            Bus.$emit('showDeleteOrganizationModal', { organization: organization });
        },

        formatDate(date, format = 'dd-MM-y HH:mm') {
            return lightFormat(parseISO(date), format);
        },

        getDaysLeft(date) {
            return differenceInCalendarDays(parseISO(date), parseISO(window.Globals.now));
        },

        dateIsInFuture(date) {
            return isAfter(parseISO(date), parseISO(window.Globals.now));
        },
    },
};
</script>
